import { db } from "./firebaseConfig";
import {
  ref, push, set, get, onValue, update, remove,
} from "firebase/database";
import type { Role } from "./RoleContext";

/** Utilisateurs */
export interface AppUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;     // identifiant
  password: string;  // provisoire (clair pour le moment)
  role: Role;        // "user" | "admin"
  active: boolean;
  createdAt: number;
}

/** Normalise une entrée venant de la DB (et filtre les valeurs bizarres) */
function normalizeUser(idFromKey: string, raw: any): AppUser | null {
  if (!raw || typeof raw !== "object") return null;
  const id = (raw.id as string) || idFromKey;
  const email = (raw.email as string)?.trim().toLowerCase() || "";
  if (!id || !email) return null; // on ignore les entrées incomplètes

  return {
    id,
    firstName: (raw.firstName as string) || "",
    lastName: (raw.lastName as string) || "",
    email,
    password: (raw.password as string) || "",
    role: (raw.role as Role) || "user",
    active: raw.active !== false,
    createdAt: typeof raw.createdAt === "number" ? raw.createdAt : 0,
  };
}

export async function addUser(params: {
  firstName: string; lastName: string; email: string; password: string; role: Role; active?: boolean;
}): Promise<string> {
  const usersRef = ref(db, "users");
  const newRef = push(usersRef);
  const id = newRef.key as string;

  const user: AppUser = {
    id,
    firstName: params.firstName.trim(),
    lastName: params.lastName.trim(),
    email: params.email.trim().toLowerCase(),
    password: params.password,
    role: params.role,
    active: params.active ?? true,
    createdAt: Date.now(),
  };
  await set(newRef, user);
  return id;
}

export async function listUsers(): Promise<AppUser[]> {
  const snap = await get(ref(db, "users"));
  const val = snap.val() || {};
  const arr: AppUser[] = Object.entries(val)
    .map(([k, v]) => normalizeUser(k, v))
    .filter((u): u is AppUser => !!u);
  return arr;
}

export function subscribeUsers(callback: (users: AppUser[]) => void): () => void {
  const usersRef = ref(db, "users");
  const unsub = onValue(usersRef, (snap) => {
    const val = snap.val() || {};
    const arr: AppUser[] = Object.entries(val)
      .map(([k, v]) => normalizeUser(k, v))
      .filter((u): u is AppUser => !!u);
    callback(arr);
  });
  return unsub;
}

export async function getUserById(id: string): Promise<AppUser | null> {
  const snap = await get(ref(db, `users/${id}`));
  return normalizeUser(id, snap.val());
}

export async function updateUserPassword(id: string, newPassword: string): Promise<void> {
  await update(ref(db, `users/${id}`), { password: newPassword });
}

export async function setUserActive(id: string, active: boolean): Promise<void> {
  await update(ref(db, `users/${id}`), { active });
}

export async function deleteUser(id: string): Promise<void> {
  await remove(ref(db, `users/${id}`));
}

/** Logs (registre d’activités) */
export type LogType =
  | "login_success"
  | "login_failure"
  | "logout"
  | "alert_collegue"
  | "alert_police"
  | "password_change";

export interface LogEntry {
  id: string;
  type: LogType;
  timestamp: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  userRole?: Role;
  agentName?: string;
  details?: string;
}

/** Filtrer les `undefined` avant d'écrire */
export async function addLog(entry: Omit<LogEntry, "id" | "timestamp"> & { timestamp?: number }) {
  const logsRef = ref(db, "logs");
  const newRef = push(logsRef);
  const id = newRef.key as string;

  const base: LogEntry = {
    id,
    type: entry.type,
    timestamp: entry.timestamp ?? Date.now(),
  };

  const payload: Record<string, any> = {
    ...base,
    email: entry.email,
    firstName: entry.firstName,
    lastName: entry.lastName,
    userRole: entry.userRole,
    agentName: entry.agentName,
    details: entry.details,
  };
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });

  await set(newRef, payload);
  return id;
}

export function subscribeLogs(callback: (logs: LogEntry[]) => void, limit: number = 500): () => void {
  const logsRef = ref(db, "logs");
  const unsub = onValue(logsRef, (snap) => {
    const val = snap.val() || {};
    const arr = Object.values(val || {}) as LogEntry[];
    arr.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    callback(arr.slice(0, limit));
  });
  return unsub;
}
